import { Flex, ListItemOption, Switch, Text } from "@theorgchartdev/storybook";
import {
  EmployeeFieldConfig,
  PositionFieldConfig,
  getFieldsFromConfig,
  getDefaultPositionFieldConfig,
  getDefaultEmployeeFieldConfig,
  RequestHeadingFieldSwapFn,
  isProfileFieldExcluded,
  ensureHeadingFieldIncluded,
  PROFILE_TEST_PREFIX
} from "./ProfileConfiguration.utils";
import { appstr } from "@coreapp/utils/appstr";
import { commonMenuProps } from "@/appComponents/Blocks/Composites/RuleInput/RulePairInput/RulePairInput";
import { FieldSelectListProps } from "@/Utils/ComponentLib";
import { NullableMenu } from "@/appComponents/Blocks/Composites/ReactInputFields/DateRangeInputField/NullableMenu";
import { CapitalizeFirstWord } from "@/Utils/DataLib";
import { useMemo } from "react";
import { getImagePath } from "@coreapp/utils/getImagePath";
import { EmployeeHeadingProfileCard } from "@/appComponents/Blocks/Cards/ProfileBaseballCard/Headers/EmployeeHeadingProfileCard";
import { PositionHeadingProfileCard } from "@/appComponents/Blocks/Cards/ProfileBaseballCard/Headers/PositionHeadingProfileCard";
import { FavoriteManagerInterface } from "@/appComponents/Blocks/Cards/ProfileBaseballCard/EmployeeBaseballCard";
import { ProfileTypes } from "@/appComponents/Blocks/Cards/ProfileBaseballCard/ProfileBaseballCard.utils";
import {
  kDivisionID,
  kEmployeeID,
  kNameID,
  kPersonID,
  kPositionDivisionID,
  kPositionID,
  kPositionTitleID,
  kTitleID
} from "@coreapp/constants";

export type HeadingConfigProps = {
  type: ProfileTypes;
  config?: any;
  isPositionChart: boolean;
  handleUpdate: (config: any, fieldChanged: string) => void;
  fields: string[];
  requestHeadingFieldSwap: RequestHeadingFieldSwapFn;
};

export const ProfileHeadingConfig = ({
  type,
  config,
  handleUpdate,
  requestHeadingFieldSwap,
  fields,
  isPositionChart
}: HeadingConfigProps) => {
  const profileConfig = config?.profile || {};
  const fieldConfig = useMemo(() => {
    if (profileConfig?.headingConfig?.[type] != null) {
      return profileConfig.headingConfig[type];
    }

    const isPositionElement = type === ProfileTypes.Position;

    // Legacy support
    if (isPositionElement) {
      return getDefaultPositionFieldConfig(config?.fields || {});
    } else {
      const defaultConfig = getDefaultEmployeeFieldConfig(isPositionChart, config?.fields || {});
      const legacyStarredFields = profileConfig?.starFields;
      if (legacyStarredFields) {
        defaultConfig.favoriteField1 = legacyStarredFields[0] || "";
        defaultConfig.favoriteField2 = legacyStarredFields[1] || "";
        defaultConfig.favoriteField3 = legacyStarredFields[2] || "";
      }

      return defaultConfig;
    }
  }, [profileConfig, config, type, isPositionChart, fields]);

  const availableFields = useMemo(() => {
    const usedFields = new Set<string>(getFieldsFromConfig(config, type));

    return fields.filter(f => !usedFields.has(f));
  }, [type, config, fields]);

  const updateFieldConfig = (value: string | boolean | undefined, key: string) => {
    const isMappingField = typeof value === "string" && value.length > 0;

    const performUpdate = () => {
      let nextFieldConfig = structuredClone(fieldConfig);
      nextFieldConfig[key] = value;

      let nextProfileMap = structuredClone(profileConfig);
      if (nextProfileMap.headingConfig == null) {
        nextProfileMap.headingConfig = {};
        delete nextProfileMap.starFields; // legacy
      }

      nextProfileMap.headingConfig[type] = nextFieldConfig;

      if (isMappingField) {
        ensureHeadingFieldIncluded(nextProfileMap, type, value as string, fields);
      }

      handleUpdate({ profile: nextProfileMap }, "profile");
    };

    if (isMappingField && isProfileFieldExcluded(value as string, config, type)) {
      requestHeadingFieldSwap(value as string, performUpdate);
    } else {
      performUpdate();
    }
  };

  const FieldOptionComponent =
    type === ProfileTypes.Employee ? EmployeeFieldOptions : PositionFieldOptions;
  const DummyProfileCard =
    type === ProfileTypes.Employee ? EmployeeDummyProfileCard : PositionDummyProfileCard;

  return (
    <>
      <Text modifier="bold" size="medium" element="h3">
        {appstr("Heading information")}
      </Text>
      <Flex direction="row" gap="16px">
        <Flex direction="column" gap="8px" flex="3">
          <DummyProfileCard
            config={fieldConfig}
            mapping={config?.fields}
            isPositionChart={isPositionChart}
          />
        </Flex>
        <Flex gap="16px" flex="7" className="oc-profile-config-field-options">
          <FieldOptionComponent
            handleUpdate={updateFieldConfig}
            fields={availableFields}
            fieldConfig={fieldConfig}
          />
        </Flex>
      </Flex>
    </>
  );
};

export const getCommonMenuPropsFields = (dataTestId: string) => {
  return {
    ...commonMenuProps,
    bold: false,
    dataTestId,
    triggerProps: {
      bold: false,
      size: "medium",
      modifier: "secondary",
      className: "oc-heading-field-input"
    },
    buttonSize: "medium",
    iconStyle: {
      right: "40px",
      width: "8px"
    },
    listProps: FieldSelectListProps({
      enableSearch: true,
      customPlaceholder: appstr("findfield"),
      inputProps: {
        wrapperDivClass: "oc-heading-field-input-search"
      }
    }),
    fillWidth: true,
    listWidth: "fill"
  };
};

type EmployeeFieldOptionsProps = {
  handleUpdate: (value: any, key: string) => void;
  fields: string[];
  fieldConfig: EmployeeFieldConfig;
};

const EmployeeFieldOptions = ({ handleUpdate, fields, fieldConfig }: EmployeeFieldOptionsProps) => {
  const getFieldOptions = (optionName: string) => {
    const currentField = fieldConfig[optionName as keyof EmployeeFieldConfig];
    const allFields = [currentField, ...fields];
    return allFields.map(field => ({
      value: field,
      label: field,
      clickHandler: (option: ListItemOption) => handleUpdate(option.value, optionName)
    }));
  };

  const nullifyValue = (optionName: string) => {
    handleUpdate(undefined, optionName);
  };

  return (
    <Flex direction="column" gap="8px" width="100%">
      <Switch
        id="oc-profile-employee-show-photo-radio"
        data-testid={`${PROFILE_TEST_PREFIX}-employee-show-photo-toggle`}
        label={appstr("Profile picture")}
        checked={fieldConfig.showProfile}
        onChange={() => handleUpdate(!fieldConfig.showProfile, "showProfile")}
      />

      <Text className="oc-rule-label oc-font-weight-500" size="small">
        {appstr("Phone")}
      </Text>
      <NullableMenu
        options={getFieldOptions("phone")}
        value={fieldConfig.phone}
        label={fieldConfig.phone}
        nullifyValue={() => nullifyValue("phone")}
        clearButtonDataTestId={`${PROFILE_TEST_PREFIX}-employee-phone-clear-button`}
        {...(getCommonMenuPropsFields(`${PROFILE_TEST_PREFIX}-employee-phone-select`) as any)}
      />

      <Text className="oc-rule-label oc-font-weight-500" size="small">
        {appstr("Location")}
      </Text>
      <NullableMenu
        options={getFieldOptions("location")}
        value={fieldConfig.location}
        label={fieldConfig.location}
        nullifyValue={() => nullifyValue("location")}
        clearButtonDataTestId={`${PROFILE_TEST_PREFIX}-employee-location-clear-button`}
        {...(getCommonMenuPropsFields(`${PROFILE_TEST_PREFIX}-employee-location-select`) as any)}
      />

      <Text className="oc-rule-label oc-font-weight-500" size="small">
        {appstr("Email")}
      </Text>
      <NullableMenu
        options={getFieldOptions("email")}
        value={fieldConfig.email}
        label={fieldConfig.email}
        nullifyValue={() => nullifyValue("email")}
        clearButtonDataTestId={`${PROFILE_TEST_PREFIX}-employee-email-clear-button`}
        {...(getCommonMenuPropsFields(`${PROFILE_TEST_PREFIX}-employee-email-select`) as any)}
      />

      <Text className="oc-rule-label oc-font-weight-500" size="small">
        {appstr("Favorite field {i}", { i: 1 })}
      </Text>
      <NullableMenu
        options={getFieldOptions("favoriteField1")}
        value={fieldConfig.favoriteField1}
        label={fieldConfig.favoriteField1}
        nullifyValue={() => nullifyValue("favoriteField1")}
        clearButtonDataTestId={`${PROFILE_TEST_PREFIX}-employee-favorite-field-1-clear-button`}
        {...(getCommonMenuPropsFields(
          `${PROFILE_TEST_PREFIX}-employee-favorite-field-1-select`
        ) as any)}
      />

      <Text className="oc-rule-label oc-font-weight-500" size="small">
        {appstr("Favorite field {i}", { i: 2 })}
      </Text>
      <NullableMenu
        options={getFieldOptions("favoriteField2")}
        value={fieldConfig.favoriteField2}
        label={fieldConfig.favoriteField2}
        nullifyValue={() => nullifyValue("favoriteField2")}
        clearButtonDataTestId={`${PROFILE_TEST_PREFIX}-employee-favorite-field-2-clear-button`}
        {...(getCommonMenuPropsFields(
          `${PROFILE_TEST_PREFIX}-employee-favorite-field-2-select`
        ) as any)}
      />

      <Text className="oc-rule-label oc-font-weight-500" size="small">
        {appstr("Favorite field {i}", { i: 3 })}
      </Text>
      <NullableMenu
        options={getFieldOptions("favoriteField3")}
        value={fieldConfig.favoriteField3}
        label={fieldConfig.favoriteField3}
        nullifyValue={() => nullifyValue("favoriteField3")}
        clearButtonDataTestId={`${PROFILE_TEST_PREFIX}-employee-favorite-field-3-clear-button`}
        {...(getCommonMenuPropsFields(
          `${PROFILE_TEST_PREFIX}-employee-favorite-field-3-select`
        ) as any)}
      />
    </Flex>
  );
};

type PositionFieldOptionsProps = {
  handleUpdate: (value: any, key: string) => void;
  fields: string[];
  fieldConfig: PositionFieldConfig;
};

const PositionFieldOptions = ({ handleUpdate, fields, fieldConfig }: PositionFieldOptionsProps) => {
  const getFieldOptions = (optionName: string) => {
    return fields.map(field => ({
      value: field,
      label: field,
      clickHandler: (option: ListItemOption) => handleUpdate(option.value, optionName)
    }));
  };

  const nullifyValue = (optionName: string) => {
    handleUpdate(undefined, optionName);
  };

  const workLocationLocalized = CapitalizeFirstWord(appstr("Work Location").toLowerCase());
  return (
    <Flex direction="column" gap="8px" width="100%">
      <Text className="oc-rule-label oc-font-weight-500" size="small">
        {workLocationLocalized}
      </Text>
      <NullableMenu
        options={getFieldOptions("workLocation")}
        value={fieldConfig.workLocation}
        label={fieldConfig.workLocation}
        nullifyValue={() => nullifyValue("workLocation")}
        clearButtonDataTestId={`${PROFILE_TEST_PREFIX}-position-work-location-clear-button`}
        {...(getCommonMenuPropsFields(
          `${PROFILE_TEST_PREFIX}-position-work-location-select`
        ) as any)}
      />

      <Text className="oc-rule-label oc-font-weight-500" size="small">
        {appstr("Position start date")}
      </Text>
      <NullableMenu
        options={getFieldOptions("positionStartDate")}
        value={fieldConfig.positionStartDate}
        label={fieldConfig.positionStartDate}
        nullifyValue={() => nullifyValue("positionStartDate")}
        clearButtonDataTestId={`${PROFILE_TEST_PREFIX}-position-start-date-clear-button`}
        {...(getCommonMenuPropsFields(`${PROFILE_TEST_PREFIX}-position-start-date-select`) as any)}
      />

      <Text className="oc-rule-label oc-font-weight-500" size="small">
        {appstr("Position end date")}
      </Text>
      <NullableMenu
        options={getFieldOptions("positionEndDate")}
        value={fieldConfig.positionEndDate}
        label={fieldConfig.positionEndDate}
        nullifyValue={() => nullifyValue("positionEndDate")}
        clearButtonDataTestId={`${PROFILE_TEST_PREFIX}-position-end-date-clear-button`}
        {...(getCommonMenuPropsFields(`${PROFILE_TEST_PREFIX}-position-end-date-select`) as any)}
      />
    </Flex>
  );
};

const OC_LOGO_SOURCE = "/static/images/OC-logo.png";
type EmployeeDummyProfileCardProps = {
  config: EmployeeFieldConfig;
  mapping: any;
  isPositionChart: boolean;
};
const EmployeeDummyProfileCard = ({
  config,
  mapping,
  isPositionChart
}: EmployeeDummyProfileCardProps) => {
  const dummyFavoriteController: FavoriteManagerInterface = {
    isStarringEnabled: false,
    getCurrentFavorites: () =>
      [config.favoriteField1, config.favoriteField2, config.favoriteField3].filter(
        Boolean
      ) as string[],
    removeStarredField: (field: { field: string; value: string }) => {},
    toggleStarredField: (field: string) => {}
  };

  const dummyObject = {
    config,
    header1Text: mapping[kNameID] || appstr("Full name"),
    showHeader1: true,
    subheader1Text: mapping[isPositionChart ? kEmployeeID : kPersonID] || appstr("Employee ID"),
    showSubheader1: true,
    header2Text: mapping[kTitleID] || appstr("Employee Title"),
    showHeader2: true,
    header3Text: mapping[kDivisionID] || appstr("Department"),
    showHeader3: true,
    photoSrc: getImagePath(OC_LOGO_SOURCE),
    showPhoto: true,
    showAssignmentStatus: true,
    favoriteController: dummyFavoriteController,

    record: {
      [config.phone]: config.phone,
      [config.location]: config.location,
      [config.email]: config.email,
      [config.favoriteField1]: config.favoriteField1,
      [config.favoriteField2]: config.favoriteField2,
      [config.favoriteField3]: config.favoriteField3
    },
    ignoreFieldDefCheck: true
  };

  return <EmployeeHeadingProfileCard {...dummyObject} />;
};

const PositionDummyProfileCard = ({
  config,
  mapping
}: {
  config: PositionFieldConfig;
  mapping: any;
}) => {
  const dummyObject = {
    config,
    header1Text: mapping[kPositionTitleID] || appstr("Position Title"),
    showHeader1: true,
    subheader1Text: mapping[kPositionID] || appstr("Position ID"),
    showSubheader1: true,
    header2Text: mapping[kPositionDivisionID] || appstr("Department"),
    showHeader2: true,

    record: {
      [config.workLocation]: config.workLocation,
      [config.positionStartDate]: config.positionStartDate,
      [config.positionEndDate]: config.positionEndDate
    }
  };

  return <PositionHeadingProfileCard {...dummyObject} />;
};
